// Updated with Confluence prism border (color-blocked) animation

import React from "react";

import { styled } from "@compiled/react";

import { N0, N200 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

// Outer wrapper for glow effect
const GlowWrapper = styled.div<{
    isLoading: boolean;
    isStreaming: boolean;
}>(({ isLoading, isStreaming }) => ({
    "position": "relative",
    "display": "flex",
    "width": "100%",
    "borderRadius": token("space.050", "4px"),
    "padding": isLoading || isStreaming ? "10px" : "10px 0 0 0",
    "margin": "-10px",
    "overflow": "hidden",
    "&::before": {
        content: "''",
        width: "1000vw",
        height: "1000vh",
        left: "calc(-500vw + 50%)",
        top: "calc(-500vh + 50%)",
        position: "absolute",
        zIndex: -2,
        borderRadius: token("space.050", "4px"),
        background:
            isLoading || isStreaming
                ? "conic-gradient(from 39deg at 50% 50%, #FCA700 0deg, #FCA700 72.69129753112793deg, #6A9A23 72.72729814052582deg, #6A9A23 167.89464354515076deg, #1868DB 167.93063879013062deg, #1868DB 252.66713619232178deg, #AF59E0 252.7031421661377deg, #AF59E0 360deg)"
                : "transparent",
        animation:
            isLoading || isStreaming ? "rotateGlow 2s linear infinite" : "none",
        filter: "blur(12px)",
        opacity: 0.5
    },
    "&::after": {
        content: "''",
        position: "absolute",
        inset: "0",
        zIndex: -1,
        background: `
            linear-gradient(to right, ${token(
                "elevation.surface.overlay",
                N0
            )} 0px, transparent 40px, transparent calc(100% - 40px), ${token(
            "elevation.surface.overlay",
            N0
        )} 100%),
            linear-gradient(to bottom, ${token(
                "elevation.surface.overlay",
                N0
            )} 0px, transparent 40px, transparent calc(100% - 40px), ${token(
            "elevation.surface.overlay",
            N0
        )} 100%)
        `,
        pointerEvents: "none"
    },
    "@keyframes rotateGlow": {
        "0%": {
            transform: "rotate(0deg)"
        },
        "100%": {
            transform: "rotate(360deg)"
        }
    },
    "@media (prefers-reduced-motion)": {
        "&::before": {
            animation: "none !important"
        }
    }
}));

// Inner wrapper for sharp border
const BorderWrapper = styled.div<{
    isLoading: boolean;
    isStreaming: boolean;
}>(({ isLoading, isStreaming }) => ({
    "display": "flex",
    "flexDirection": "column",
    "position": "relative",
    "borderRadius": `${token("space.050", "4px")}`,
    "backgroundColor": token("elevation.surface.overlay", N0),
    "padding": "0",
    "boxSizing": "border-box",
    "overflow": "hidden",
    "zIndex": 1,
    "width": "100%",
    "&::before": {
        content: "''",
        width: "1000vw",
        height: "1000vh",
        left: "calc(-500vw + 50%)",
        top: "calc(-500vh + 50%)",
        position: "absolute",
        zIndex: -2,
        borderRadius: token("space.050", "4px"),
        background:
            isLoading || isStreaming
                ? "conic-gradient(from 39deg at 50% 50%, #FCA700 0deg, #FCA700 72.69129753112793deg, #6A9A23 72.72729814052582deg, #6A9A23 167.89464354515076deg, #1868DB 167.93063879013062deg, #1868DB 252.66713619232178deg, #AF59E0 252.7031421661377deg, #AF59E0 360deg)"
                : "transparent",
        animation:
            isLoading || isStreaming
                ? "rotateBorder 2s linear infinite"
                : "none"
    },
    "&::after": {
        content: "''",
        width: "calc(100% - 2px)",
        height: "calc(100% - 2px)",
        inset: "1px",
        position: "absolute",
        zIndex: -1,
        borderRadius: token("space.050", "4px"),
        backgroundColor: token("elevation.surface.overlay", N0)
    },
    "@keyframes rotateBorder": {
        "0%": {
            transform: "rotate(0deg)"
        },
        "100%": {
            transform: "rotate(360deg)"
        }
    },
    "@media (prefers-reduced-motion)": {
        "&::before": {
            animation: "none !important"
        }
    }
}));

// Combined component that wraps both
export const AIAnswerSearchBorder = ({
    isLoading,
    isStreaming,
    children
}: {
    isLoading: boolean;
    isStreaming: boolean;
    children: React.ReactNode;
}) => {
    return (
        <GlowWrapper isLoading={isLoading} isStreaming={isStreaming}>
            <BorderWrapper isLoading={isLoading} isStreaming={isStreaming}>
                {children}
            </BorderWrapper>
        </GlowWrapper>
    );
};

export const AIAnswerLoadingSkeletonWrapper = styled.div({
    display: "flex",
    flexDirection: "column",
    marginTop: token("space.200", "16px"),
    gap: token("space.100", "8px"),
    width: "100%"
});

export const ReadingAidsLoadingPill = styled.div<{
    width?: string;
    height?: string;
}>(({ width, height }) => ({
    "display": "flex",

    "width": width ? width : "100%",

    "height": height ? height : "16px",
    "borderRadius": token("border.radius.200"),
    "background": `linear-gradient(to right, ${token(
        "color.background.neutral",
        "rgba(240, 241, 244, 1)"
    )}, ${token("color.background.input.pressed", "rgba(252, 252, 252, 1)")})`,
    "animation": "shimmer 1.5s linear infinite alternate",

    "@keyframes shimmer": {
        from: {
            background: `linear-gradient(to right, ${token(
                "color.background.neutral",
                "rgba(240, 241, 244, 1)"
            )}, ${token(
                "color.background.input.pressed",
                "rgba(252, 252, 252, 1)"
            )})`
        },
        to: {
            background: `linear-gradient(to right, ${token(
                "color.background.neutral",
                "rgba(240, 241, 244, 1)"
            )}, ${token(
                "color.background.input.pressed",
                "rgba(252, 252, 252, 1)"
            )})`
        }
    }
}));

export const GeneratingContainer = styled.div({
    display: "flex",
    height: "92px",
    boxSizing: "border-box",
    paddingTop: token("space.300", "24px"),
    paddingRight: token("space.300", "24px"),
    paddingBottom: "0px",
    paddingLeft: token("space.300", "24px")
});

export const GeneratingText = styled.div({
    marginLeft: token("space.025", "2px"),

    color: token("color.text.subtlest", N200),
    font: token("font.body")
});

export const ToggleButton = styled.div({
    cursor: "pointer",
    display: "block"
});

export const ButtonIcon = styled.div({
    display: "block",
    transform: "rotate(135deg)"
});

export const Blur = styled.div({
    background: `linear-gradient( 180deg, ${token(
        "utility.UNSAFE.transparent",
        "rgba(255, 255, 255, 0)"
    )} 0%, ${token("elevation.surface.overlay", N0)} 100% )`,
    bottom: "0",
    display: "block",
    height: token("space.500", "40px"),
    margin: "0 1px",
    position: "sticky"
});

export const AISourcesWrapper = styled.div({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: token("space.100"),
    paddingTop: 0,
    paddingRight: token("space.300"),
    paddingBottom: 0,
    paddingLeft: token("space.300")
});
